import { Injectable } from '@angular/core';
import { UserProfileApiServiceService } from './user-profile-api-service.service';
import { signal } from '@angular/core';
import { User } from 'src/app/models/core/user.model';
import { Observable, firstValueFrom } from 'rxjs';
import { AuthService } from '../auth.service';
import { BiometricService } from '../biometric.service';

@Injectable({
  providedIn: 'root'
})
export class UserProfileServiceService {
  userProfile = signal<User | null>(null);
  isLoading = signal(false);
  isUpdateLoading = signal(false);

  private normEmail = (v?: string | null) => (v ?? '').trim().toLowerCase();
  private norm = (v?: string | null) => (v ?? '').trim();

  constructor(
    private userProfileApi: UserProfileApiServiceService,
    private authService: AuthService,
    private biometricService: BiometricService,
  ) { }

  async initializeFromLogin(user: User): Promise<void> {
    this.userProfile.set(user);
    console.log('User profile initialized from login', user);
  }

  async initializeFromStorage(): Promise<User | null> {
    try {
      const authData = await this.authService.getLogin();
      if (authData?.user) {
        if (!this.userProfile()) {
          this.userProfile.set(authData.user);
          console.log('User profile loaded from storage (initial load)', authData.user);
          return authData.user;
        }
      }
      return null;
    } catch (error) {
      console.error('Error loading user from storage:', error);
      return null;
    }
  }

  async updateStorageWithCurrentData(): Promise<void> {
    try {
      const currentUser = this.userProfile();
      if (!currentUser) {
        console.warn('No user data available to sync with storage');
        return;
      }

      const authData = await this.authService.getLogin();
      if (!authData) {
        console.warn('No auth data found in storage, cannot update');
        return;
      }

      const isInSync = JSON.stringify(currentUser) === JSON.stringify(authData.user);
      if (isInSync) {
        console.log('Storage already in sync with current user data');
        return;
      }

      authData.user = currentUser;
      await this.authService.saveLogin(authData);
    } catch (error) {
      console.error('Error updating storage with current data', error);
      throw error;
    }
  }

  loadProfile(): Observable<User> {
    this.isLoading.set(true);
    return this.userProfileApi.getProfile();
  }

  async loadProfileAsync(): Promise<User> {
    this.isLoading.set(true);
    try {
      const data = await firstValueFrom(this.userProfileApi.getProfile());
      this.userProfile.set(data);

      await this.updateStorageWithCurrentData();

      return data;
    } catch (error) {
      console.error('Error loading profile:', error);
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  }

  updateProfile(data: Partial<Record<keyof User, string | null>>): Observable<User> {
    return this.userProfileApi.updateProfile(data);
  }

  async updateProfileAsync(data: Partial<Record<keyof User, string | null>>): Promise<User> {
    this.isUpdateLoading.set(true);
    const before = this.userProfile();
    const beforeEmail = this.normEmail(before?.email);
    const beforeUser = this.norm(before?.username);

    try {
      const updated = await firstValueFrom(this.userProfileApi.updateProfile(data));
      this.userProfile.set(updated);
      await this.updateStorageWithCurrentData();

      const afterEmail = this.normEmail(updated?.email);
      const afterUser = this.norm(updated?.username);

      const emailChanged = !!beforeEmail && !!afterEmail && beforeEmail !== afterEmail;
      const userChanged = !!beforeUser && !!afterUser && beforeUser !== afterUser;

      const bio = await this.biometricService.getBiometricCredentials();
      const stored = this.norm(bio?.username);

      if (stored && stored === beforeEmail && emailChanged) {
        await this.biometricService.rebindWithNewCredential(afterEmail);
        return updated;
      }

      if (stored && stored === beforeUser && userChanged) {
        await this.biometricService.rebindWithNewCredential(afterUser);
        return updated;
      }

      if (emailChanged && !userChanged) {
        await this.biometricService.rebindWithNewCredential(afterEmail);
        return updated;
      }
      if (!emailChanged && userChanged) {
        await this.biometricService.rebindWithNewCredential(afterUser);
        return updated;
      }

      const beforeWereEqual = !!beforeEmail && beforeEmail === this.normEmail(beforeUser);
      if (beforeWereEqual && emailChanged && userChanged) {
        await this.biometricService.rebindWithNewCredential(afterUser);
        return updated;
      }

      return updated;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    } finally {
      this.isUpdateLoading.set(false);
    }
  }


  clearProfile() {
    this.userProfile.set(null);
  }

  hasUserData(): boolean {
    return this.userProfile() !== null;
  }

  getUserData(): User | null {
    return this.userProfile();
  }

  async syncStorageWithCurrentData(): Promise<void> {
    await this.updateStorageWithCurrentData();
  }

  async isStorageInSync(): Promise<boolean> {
    try {
      const currentUser = this.userProfile();
      if (!currentUser) return false;

      const authData = await this.authService.getLogin();
      if (!authData?.user) return false;

      return JSON.stringify(currentUser) === JSON.stringify(authData.user);
    } catch (error) {
      console.error('Error checking storage sync:', error);
      return false;
    }
  }

  async ensureStorageSync(): Promise<void> {
    try {
      const isInSync = await this.isStorageInSync();
      if (!isInSync) {
        console.log('Storage out of sync, updating...');
        await this.updateStorageWithCurrentData();
      }
    } catch (error) {
      console.error('Error ensuring storage sync:', error);
    }
  }

  /**
   * Check if the user has all the required data
   */
  hasRequiredData(): boolean {
    const user = this.userProfile();
    if (!user) return false;

    return !!(user.dni && user.birthday && user.phone);
  }

}
